/**
 * Algo Engine single-algo management page (Phase 1, admin-only).
 *
 * This is NOT a second catalogue — it is the detail/management view for
 * whichever single `Algo` row has a `strategy_key` wired up (TEK-MB01 /
 * "Velocity Edge" today; future algos reuse this same page once they get
 * their own `strategy_key`). Reached via `/admin/algos/:algoId`, linked
 * only from that row's CTA button on the public `StrategiesPage`.
 *
 * Three tabs:
 *   - Backtest: run the replay engine, inspect summary + trades. Config
 *     version selection was removed 2026-09-19 "Simplify Algo Backtest
 *     design" — a backtest always runs against the algo's currently
 *     active config, resolved server-side.
 *   - Performance: the Algo Performance Dashboard (2026-09-19) — a
 *     Backtest/Live toggle, 5 KPI cards, and an equity curve, reading
 *     precomputed snapshots only (see `AlgoPerformanceTab.tsx`).
 *   - Live Monitor: runtime controls (execution status, entries-pause,
 *     emergency exit, global kill switch) + positions/orders/signals.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Chart as ChartJS, LinearScale, LineElement, PointElement, CategoryScale, Tooltip as ChartTooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import algoService from "@/services/algoService";
import algoBacktestService from "@/services/algoBacktestService";
import algoExecutionService from "@/services/algoExecutionService";
import { AlgoPerformanceTab } from "@/pages/admin/AlgoPerformanceTab";
import type {
  Algo,
  AlgoBacktestDiagnostics,
  AlgoBacktestRun,
  AlgoBacktestTrade,
  AlgoOrder,
  AlgoPosition,
  AlgoSignalLog,
  GlobalSettingsRead,
  RuntimeStatusResponse,
} from "@/types";
import { extractErrorMessage } from "@/utils/errors";

// Chart.js + `react-chartjs-2` is this codebase's only installed charting
// library (see `PayoffChart.tsx`, `HistoryCharts.tsx`) — registered once at
// module scope, same pattern as those.
ChartJS.register(LinearScale, CategoryScale, PointElement, LineElement, ChartTooltip, Legend);

type TabKey = "backtest" | "performance" | "monitor";

function StatusChip({ label, color }: { label: string; color: "success" | "default" | "warning" | "error" }) {
  return <Chip label={label} color={color} size="small" />;
}

// `AlgoSignalLog.candle_start` (and every other Algo Engine timestamp) is
// stored/serialized as timezone-aware UTC (see `models/algo_signal_log.py`
// `DateTime(timezone=True)`). The Live Monitor is read by IST-based traders,
// so render it converted to IST rather than the raw UTC ISO string.
function formatIst(isoTimestamp: string): string {
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return isoTimestamp;
  return `${parsed.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })} IST`;
}

// Risk-check price-staleness fix, admin-visible indicator (rollout step 6 --
// see risk_check_price_staleness_fix_design.md). `source`/`ageSeconds` are
// both `null` for every position unless ALGO_LIVE_OPTION_TICKS_ENABLED is on,
// in which case this renders "—" -- exactly today's (pre-fix) blank state,
// never a misleading placeholder.
function PriceSourceIndicator({
  source,
  ageSeconds,
}: {
  source?: "LIVE_TICK" | "OI_SNAPSHOT" | null;
  ageSeconds?: string | null;
}) {
  if (!source) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const age = ageSeconds != null ? Number(ageSeconds) : null;
  const ageLabel = age != null ? `${age.toFixed(1)}s ago` : "no tick received yet";
  if (source === "LIVE_TICK") {
    return (
      <Tooltip title={`Last risk check used a live WebSocket tick (${ageLabel}).`}>
        <Chip label="Live tick" size="small" color="success" />
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={`Last risk check fell back to the OI-scan snapshot (last live tick: ${ageLabel}).`}
    >
      <Chip label="OI snapshot" size="small" color="warning" />
    </Tooltip>
  );
}

export function AlgoManagementPage() {
  const { algoId } = useParams<{ algoId: string }>();
  const navigate = useNavigate();

  const [algo, setAlgo] = useState<Algo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("backtest");

  const [runtimeStatus, setRuntimeStatus] = useState<RuntimeStatusResponse | null>(null);
  const [globalSettings, setGlobalSettings] = useState<GlobalSettingsRead | null>(null);

  const loadAlgoAndRuntime = useCallback(async () => {
    if (!algoId) return;
    const [algoData, runtime, global] = await Promise.all([
      algoService.admin.getById(algoId),
      algoExecutionService.getRuntimeStatus(algoId),
      algoExecutionService.getGlobalSettings(),
    ]);
    setAlgo(algoData);
    setRuntimeStatus(runtime);
    setGlobalSettings(global);
  }, [algoId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await loadAlgoAndRuntime();
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [loadAlgoAndRuntime]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !algo) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!algo || !algoId) {
    return <Alert severity="warning">Algo not found.</Alert>;
  }

  if (!algo.strategy_key) {
    return (
      <Alert severity="warning">
        "{algo.algo_name}" has no Algo Engine strategy wired up yet. This management page only applies to algos with a
        `strategy_key` set.
      </Alert>
    );
  }

  return (
    <Box>
      <Button size="small" onClick={() => navigate("/strategies")} sx={{ mb: 2 }}>
        &larr; Back to Strategies
      </Button>

      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        <Typography variant="h4" fontWeight={700}>
          {algo.algo_name}
        </Typography>
        <Chip label={algo.strategy_key} size="small" variant="outlined" />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Algo Engine management (Phase 1) — Paper Trading only. Real broker execution stays disabled until both the
        environment-level and global kill switches are enabled.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {runtimeStatus && globalSettings && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1} alignItems="center">
            <StatusChip
              label={`Execution: ${runtimeStatus.execution_status}`}
              color={runtimeStatus.execution_status === "LIVE" ? "warning" : "default"}
            />
            <StatusChip
              label={`Global kill switch: ${globalSettings.live_execution_enabled ? "ENABLED" : "disabled"}`}
              color={globalSettings.live_execution_enabled ? "error" : "success"}
            />
            <StatusChip
              label={`Real orders allowed: ${runtimeStatus.live_execution_globally_enabled ? "YES" : "no"}`}
              color={runtimeStatus.live_execution_globally_enabled ? "error" : "success"}
            />
            {runtimeStatus.active_config_version != null && (
              <StatusChip label={`Active config v${runtimeStatus.active_config_version}`} color="default" />
            )}
            <StatusChip
              label={`New entries: ${runtimeStatus.entries_paused ? "PAUSED" : "active"}`}
              color={runtimeStatus.entries_paused ? "warning" : "success"}
            />
          </Stack>
        </Paper>
      )}

      <Tabs value={tab} onChange={(_e, value) => setTab(value)} sx={{ mb: 3 }}>
        <Tab value="backtest" label="Backtest" />
        <Tab value="performance" label="Performance" />
        <Tab value="monitor" label="Live Monitor" />
      </Tabs>

      {tab === "backtest" && <BacktestTab algoId={algoId} />}
      {tab === "performance" && <AlgoPerformanceTab algoId={algoId} />}
      {tab === "monitor" && (
        <MonitorTab
          algoId={algoId}
          runtimeStatus={runtimeStatus}
          globalSettings={globalSettings}
          onChanged={loadAlgoAndRuntime}
        />
      )}
    </Box>
  );
}

const BACKTEST_TERMINAL_STATUSES = ["COMPLETED", "FAILED"];
const BACKTEST_POLL_INTERVAL_MS = 3000;

// ---- Backtest Result UI helpers (Phase 2, 2026-09-19 "Improve Backtest
// Result UI") ----
// Every value below either reads a field `_build_summary()`
// (`app/services/algo_backtest/replay_service.py`) already returns on
// `AlgoBacktestRunRead.summary`, or is a plain arithmetic derivation of two
// such already-returned numbers — never a new backend calculation. The two
// derived values are:
//   - "Missing data %" = 100 - (one_minute_candle_pct + five_minute_derived_candle_pct),
//     the arithmetic complement of the two coverage percentages the backend
//     already returns in `resolution_note`.
//   - "Average trade" = net_pnl / total_trades, from two already-returned
//     summary numbers.
// "Return %" is deliberately NOT shown anywhere below — there is no
// starting-capital/capital-base field anywhere in `AlgoBacktestRunRead` or
// `_build_summary()` to divide P&L by, so it cannot be honestly computed
// here. See the Phase 2 completion report for this and other gaps.

function fmtInr(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}₹${Math.abs(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

function fmtInt(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-IN");
}

function fmtDurationMinutes(totalMinutes: number | null | undefined): string {
  if (totalMinutes === null || totalMinutes === undefined || Number.isNaN(totalMinutes)) return "—";
  const totalMin = Math.round(totalMinutes);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

interface BacktestSummary {
  total_trades?: number;
  wins?: number;
  losses?: number;
  win_rate_pct?: number;
  avg_win?: number;
  avg_loss?: number;
  net_pnl?: number;
  max_drawdown?: number;
  profit_factor?: number | null;
  avg_trade_duration_minutes?: number;
  resolution_note?: { one_minute_candle_pct?: number; five_minute_derived_candle_pct?: number };
}

type DataQualityStatus = "Good" | "Partial" | "Insufficient";

// Thresholds are a Phase 2 UI-only heuristic for labeling coverage, not a
// backend-validated classification — tune here if different cutoffs are
// wanted; flagged as such in the Phase 2 completion report.
const DATA_QUALITY_GOOD_MIN_PCT = 90;
const DATA_QUALITY_PARTIAL_MIN_PCT = 50;

function classifyDataQuality(availablePct: number): DataQualityStatus {
  if (availablePct >= DATA_QUALITY_GOOD_MIN_PCT) return "Good";
  if (availablePct >= DATA_QUALITY_PARTIAL_MIN_PCT) return "Partial";
  return "Insufficient";
}

function dataQualityChipColor(status: DataQualityStatus): "success" | "warning" | "error" {
  if (status === "Good") return "success";
  if (status === "Partial") return "warning";
  return "error";
}

function tradeDurationMinutes(t: AlgoBacktestTrade): number | null {
  if (!t.exit_time) return null;
  const entry = new Date(t.entry_time).getTime();
  const exit = new Date(t.exit_time).getTime();
  if (Number.isNaN(entry) || Number.isNaN(exit)) return null;
  return (exit - entry) / 60000;
}

// ---- Signal Diagnostics (Phase 3 "Strategy Diagnostics", 2026-09-19) ----
// Pure presentation over `AlgoBacktestDiagnostics`, fetched by
// `algoBacktestService.getDiagnostics` — every count/label here is exactly
// what `diagnostics_service.build_backtest_diagnostics` (backend) already
// computed; nothing is recomputed or re-derived in this component.

const ZERO_TRADE_EXPLANATION_LABELS: Record<string, string> = {
  NO_SIGNALS: "No Signals",
  SIGNALS_REJECTED: "Signals Rejected",
  INSUFFICIENT_DATA: "Insufficient Data",
  ENGINE_FAILURE: "Engine Failure",
  OPEN_AT_WINDOW_END: "Open At Window End",
};

function zeroTradeSeverity(code: string): "error" | "warning" | "info" {
  if (code === "ENGINE_FAILURE") return "error";
  if (code === "INSUFFICIENT_DATA" || code === "SIGNALS_REJECTED") return "warning";
  return "info";
}

function SignalDiagnosticsPanel({
  diagnostics,
  showConditionFunnel,
  onToggleFunnel,
}: {
  diagnostics: AlgoBacktestDiagnostics;
  showConditionFunnel: boolean;
  onToggleFunnel: () => void;
}) {
  const topRejectionReasons = diagnostics.rejection_reason_breakdown
    .slice(0, 2)
    .map((r) => `${r.reason} (${fmtInt(r.count)})`)
    .join(", ");

  return (
    <Stack spacing={2}>
      {diagnostics.zero_trade_explanation && (
        <Alert severity={zeroTradeSeverity(diagnostics.zero_trade_explanation)}>
          <strong>{ZERO_TRADE_EXPLANATION_LABELS[diagnostics.zero_trade_explanation] ?? diagnostics.zero_trade_explanation}.</strong>{" "}
          {diagnostics.zero_trade_explanation_detail}
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Candles processed
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {fmtInt(diagnostics.candles_processed)}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Entry signals
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {fmtInt(diagnostics.entry_signals_generated)}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Trades generated
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {fmtInt(diagnostics.trades_closed)}
          </Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="caption" color="text.secondary">
            Signals rejected
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {fmtInt(diagnostics.signals_rejected)}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={8}>
          <Typography variant="caption" color="text.secondary">
            Top rejection reasons
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {topRejectionReasons || "—"}
          </Typography>
        </Grid>
      </Grid>

      <Button size="small" onClick={onToggleFunnel} sx={{ alignSelf: "flex-start" }}>
        {showConditionFunnel ? "Hide condition detail" : "Show condition detail"}
      </Button>

      {showConditionFunnel && (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Valid / Invalid candles
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {fmtInt(diagnostics.valid_candles)} / {fmtInt(diagnostics.invalid_candles)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Entry conditions evaluated
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {fmtInt(diagnostics.entry_conditions_evaluated)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Trades opened / closed
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {fmtInt(diagnostics.trades_opened)} / {fmtInt(diagnostics.trades_closed)}
              </Typography>
            </Grid>
          </Grid>

          {diagnostics.condition_funnel.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Condition funnel (this strategy's own configured checks, in evaluation order)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Condition</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell align="right">Evaluated</TableCell>
                      <TableCell align="right">Passed</TableCell>
                      <TableCell align="right">Pass rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diagnostics.condition_funnel.map((step, idx) => (
                      <TableRow key={`${step.name}-${step.direction}-${idx}`}>
                        <TableCell>{step.name}</TableCell>
                        <TableCell>{step.direction}</TableCell>
                        <TableCell align="right">{fmtInt(step.evaluated_count)}</TableCell>
                        <TableCell align="right">{fmtInt(step.passed_count)}</TableCell>
                        <TableCell align="right">
                          {step.evaluated_count > 0 ? fmtPct((step.passed_count / step.evaluated_count) * 100) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {diagnostics.rejection_reason_breakdown.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Rejection reasons
              </Typography>
              <Stack spacing={0.5}>
                {diagnostics.rejection_reason_breakdown.map((r) => (
                  <Typography variant="body2" key={r.reason}>
                    {r.reason}: {fmtInt(r.count)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {diagnostics.exit_signal_breakdown.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Exit reasons (closed trades)
              </Typography>
              <Stack spacing={0.5}>
                {diagnostics.exit_signal_breakdown.map((r) => (
                  <Typography variant="body2" key={r.reason}>
                    {r.reason}: {fmtInt(r.count)}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function BacktestTab({ algoId }: { algoId: string }) {
  const [runs, setRuns] = useState<AlgoBacktestRun[]>([]);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<AlgoBacktestRun | null>(null);
  const [trades, setTrades] = useState<AlgoBacktestTrade[]>([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  // Phase 3 "Strategy Diagnostics" (2026-09-19) — fetched lazily, only once
  // the "Signal Diagnostics" subsection under Advanced Diagnostics is
  // actually expanded (see `handleDiagnosticsExpand` below), not on every
  // status poll.
  const [diagnostics, setDiagnostics] = useState<AlgoBacktestDiagnostics | null>(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [diagnosticsError, setDiagnosticsError] = useState<string | null>(null);
  const [showConditionFunnel, setShowConditionFunnel] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Backtest execution is async (2026-09-19 "Fix reliability" — see
  // algoBacktestService's own docstring for why): a run this tab just
  // queued, or an in-progress one picked from "Past runs", is polled here
  // until it reaches a terminal status, so the page never has to just sit
  // on a spinner waiting for one HTTP response the way it used to (which
  // is exactly what caused the 504 on a long date range).
  useEffect(() => stopPolling, [stopPolling]);

  const load = useCallback(async () => {
    try {
      const list = await algoBacktestService.listForAlgo(algoId);
      setRuns(list);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [algoId]);

  useEffect(() => {
    load();
  }, [load]);

  const startPolling = useCallback(
    (runId: string) => {
      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        try {
          const updated = await algoBacktestService.getRun(algoId, runId);
          setSelectedRun(updated);
          setRuns((prev) => prev.map((r) => (r.id === runId ? updated : r)));
          if (BACKTEST_TERMINAL_STATUSES.includes(updated.status)) {
            stopPolling();
            const t = await algoBacktestService.listTrades(algoId, runId);
            setTrades(t);
          }
        } catch {
          // Best-effort poll tick — a transient failure just tries again
          // on the next interval rather than surfacing an error banner
          // for what's likely a momentary network blip.
        }
      }, BACKTEST_POLL_INTERVAL_MS);
    },
    [algoId, stopPolling]
  );

  async function handleRun() {
    setRunning(true);
    setError(null);
    try {
      const run = await algoBacktestService.run(algoId, { from_date: fromDate, to_date: toDate });
      await load();
      await handleSelectRun(run);
      if (!BACKTEST_TERMINAL_STATUSES.includes(run.status)) {
        startPolling(run.id);
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  async function handleSelectRun(run: AlgoBacktestRun) {
    stopPolling();
    setSelectedRun(run);
    // A different run's diagnostics are never carried over onto the newly
    // selected one — re-fetched fresh if/when its own Signal Diagnostics
    // subsection is expanded.
    setDiagnostics(null);
    setDiagnosticsError(null);
    setShowConditionFunnel(false);
    if (!BACKTEST_TERMINAL_STATUSES.includes(run.status)) {
      // Picking an already QUEUED/RUNNING run from "Past runs" resumes
      // watching it, rather than showing a stale snapshot forever.
      setTrades([]);
      startPolling(run.id);
      return;
    }
    setLoadingTrades(true);
    try {
      const t = await algoBacktestService.listTrades(algoId, run.id);
      setTrades(t);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoadingTrades(false);
    }
  }

  async function handleDiagnosticsExpand(_event: unknown, expanded: boolean) {
    if (!expanded || !selectedRun || diagnostics !== null || loadingDiagnostics) return;
    setLoadingDiagnostics(true);
    setDiagnosticsError(null);
    try {
      const d = await algoBacktestService.getDiagnostics(algoId, selectedRun.id);
      setDiagnostics(d);
    } catch (err) {
      setDiagnosticsError(extractErrorMessage(err));
    } finally {
      setLoadingDiagnostics(false);
    }
  }

  const summary = selectedRun?.summary as BacktestSummary | undefined;
  const resolutionNote = summary?.resolution_note;
  const isInProgress = selectedRun != null && !BACKTEST_TERMINAL_STATUSES.includes(selectedRun.status);
  const progressPct =
    selectedRun?.progress_total != null && selectedRun.progress_total > 0
      ? Math.min(100, Math.round(((selectedRun.progress_current ?? 0) / selectedRun.progress_total) * 100))
      : null;

  const oneMinPct = resolutionNote?.one_minute_candle_pct;
  const fiveMinPct = resolutionNote?.five_minute_derived_candle_pct;
  const hasCoverageData = oneMinPct != null && fiveMinPct != null;
  const availablePct = hasCoverageData ? Math.min(100, (oneMinPct ?? 0) + (fiveMinPct ?? 0)) : null;
  const missingPct = availablePct != null ? Math.max(0, 100 - availablePct) : null;
  const dataQualityStatus = availablePct != null ? classifyDataQuality(availablePct) : null;

  const totalTrades = summary?.total_trades ?? 0;
  const avgTrade = summary && totalTrades > 0 && summary.net_pnl != null ? summary.net_pnl / totalTrades : null;
  const isZeroTradeResult = selectedRun?.status === "COMPLETED" && totalTrades === 0;

  // Equity curve: a client-side running total of each already-persisted
  // trade's own `realized_pnl`, ordered by `exit_time` — no new backtest
  // calculation, just a cumulative sum for the chart (see the helpers'
  // block comment above).
  const equityCurve = useMemo(() => {
    const closed = trades
      .filter((t) => t.exit_time != null && t.realized_pnl != null)
      .slice()
      .sort((a, b) => new Date(a.exit_time as string).getTime() - new Date(b.exit_time as string).getTime());
    let cumulative = 0;
    return closed.map((t, idx) => {
      cumulative += Number(t.realized_pnl);
      return { seq: idx + 1, cumulativePnl: cumulative, exitTime: t.exit_time as string };
    });
  }, [trades]);

  const equityChartData = useMemo(
    () => ({
      labels: equityCurve.map((p) => `#${p.seq}`),
      datasets: [
        {
          label: "Cumulative P&L",
          data: equityCurve.map((p) => p.cumulativePnl),
          borderColor: "#0F3D63",
          backgroundColor: "#0F3D63",
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.1,
        },
      ],
    }),
    [equityCurve]
  );

  const equityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "nearest" as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item: { parsed: { y: number } }) => fmtInr(item.parsed.y),
        },
      },
    },
    scales: {
      x: { title: { display: true, text: "Trade #", font: { size: 11 } } },
      y: {
        title: { display: true, text: "Cumulative P&L (₹)", font: { size: 11 } },
        ticks: { callback: (v: number | string) => fmtInr(Number(v)) },
      },
    },
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Run a backtest
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label="From date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={handleRun} disabled={running}>
              {running ? "Queuing…" : "Run backtest"}
            </Button>
            <Tooltip title="The replay engine always prefers 1-minute-resolution historical candles, falling back to 5-minute-derived candles when 1-minute data isn't available for a period — see the Data Quality section below for what this run actually got.">
              <Typography variant="caption" color="text.secondary">
                Historical data interval: 1-minute (5-minute fallback)
              </Typography>
            </Tooltip>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Past runs
          </Typography>
          {runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No backtests run yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {runs.map((run) => {
                const runSummary = run.summary as BacktestSummary | undefined;
                return (
                  <Box
                    key={run.id}
                    onClick={() => handleSelectRun(run)}
                    sx={{
                      cursor: "pointer",
                      p: 1,
                      borderRadius: 1,
                      bgcolor: selectedRun?.id === run.id ? "action.selected" : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">
                        {run.from_date} &rarr; {run.to_date} (v{run.config_version})
                      </Typography>
                      <Chip
                        label={run.status}
                        size="small"
                        color={
                          run.status === "COMPLETED"
                            ? "success"
                            : run.status === "FAILED"
                              ? "error"
                              : run.status === "RUNNING"
                                ? "info"
                                : "default"
                        }
                      />
                    </Stack>
                    {run.status === "COMPLETED" && runSummary && (
                      <Typography variant="caption" color="text.secondary">
                        {fmtInt(runSummary.total_trades)} trades &middot; Net P&amp;L {fmtInr(runSummary.net_pnl)}
                        {" · Return % — n/a"}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={7}>
        {selectedRun && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Backtest Status
            </Typography>
            {isInProgress ? (
              <Stack spacing={1.5}>
                <Alert severity="info" icon={false}>
                  {selectedRun.status === "QUEUED"
                    ? "Queued — waiting for the backtest to start…"
                    : progressPct !== null
                      ? `Running — ${selectedRun.progress_current} / ${selectedRun.progress_total} candles processed (${progressPct}%)`
                      : "Running — fetching historical candles…"}
                </Alert>
                <LinearProgress
                  variant={progressPct !== null ? "determinate" : "indeterminate"}
                  value={progressPct ?? undefined}
                />
              </Stack>
            ) : selectedRun.status === "FAILED" ? (
              <Alert severity="error">
                {selectedRun.error_message
                  ? `Backtest failed: ${selectedRun.error_message}`
                  : "Backtest failed — no further detail was recorded for this run."}
              </Alert>
            ) : selectedRun.status === "COMPLETED" ? (
              <Alert severity="success" icon={false}>
                Completed{" "}
                {selectedRun.completed_at ? `— ${formatIst(selectedRun.completed_at)}` : ""}
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {selectedRun.status}
              </Typography>
            )}
          </Paper>
        )}

        {selectedRun && !isInProgress && selectedRun.status === "COMPLETED" && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Data Quality
            </Typography>
            {hasCoverageData && availablePct != null && missingPct != null && dataQualityStatus != null ? (
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Required interval
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    1-minute
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Available coverage
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {fmtPct(availablePct)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Missing data
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {fmtPct(missingPct)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Status
                  </Typography>
                  <Chip label={dataQualityStatus} size="small" color={dataQualityChipColor(dataQualityStatus)} />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No candle-resolution data recorded for this run.
              </Typography>
            )}
          </Paper>
        )}

        {selectedRun && !isInProgress && selectedRun.status === "COMPLETED" && summary && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Performance Summary
            </Typography>

            {dataQualityStatus === "Insufficient" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Historical data coverage for this period was insufficient ({fmtPct(missingPct)} missing) — a{" "}
                {fmtInt(totalTrades)}-trade result here is not a reliable signal that the strategy wouldn't have
                traded with complete data.
              </Alert>
            )}
            {isZeroTradeResult && dataQualityStatus !== "Insufficient" && (
              <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                No trades generated during the selected period.
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Net P&amp;L
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {fmtInr(summary.net_pnl)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Return %
                </Typography>
                <Typography variant="body1" fontWeight={700} color="text.secondary">
                  N/A
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Total Trades
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {fmtInt(summary.total_trades)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Win Rate
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {totalTrades > 0 ? fmtPct(summary.win_rate_pct) : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Profit Factor
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {summary.profit_factor != null ? summary.profit_factor.toFixed(2) : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Max Drawdown
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {totalTrades > 0 ? fmtInr(summary.max_drawdown) : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="caption" color="text.secondary">
                  Average Trade
                </Typography>
                <Typography variant="body1" fontWeight={700}>
                  {fmtInr(avgTrade)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        {selectedRun &&
          !isInProgress &&
          selectedRun.status === "COMPLETED" &&
          !loadingTrades &&
          equityCurve.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                Equity Curve
              </Typography>
              <Box sx={{ height: { xs: 220, sm: 260 } }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Line data={equityChartData as any} options={equityChartOptions as any} />
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Running total of each closed trade's own realized P&amp;L, in exit order.
              </Typography>
            </Paper>
          )}

        {selectedRun && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Trade History
            </Typography>
            {isInProgress ? (
              <Typography variant="body2" color="text.secondary">
                Trades will appear here once the backtest finishes.
              </Typography>
            ) : loadingTrades ? (
              <CircularProgress size={24} />
            ) : trades.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {selectedRun.status === "COMPLETED"
                  ? "No trades generated during the selected period."
                  : "No trades for this run."}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Strike</TableCell>
                      <TableCell>Entry</TableCell>
                      <TableCell>Exit</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell align="right">P&amp;L</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trades.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>{t.option_type}</TableCell>
                        <TableCell>{fmtInt(Number(t.strike))}</TableCell>
                        <TableCell>{fmtInr(Number(t.entry_price))}</TableCell>
                        <TableCell>{t.exit_price != null ? fmtInr(Number(t.exit_price)) : "—"}</TableCell>
                        <TableCell>{fmtDurationMinutes(tradeDurationMinutes(t))}</TableCell>
                        <TableCell>{t.exit_reason ?? "—"}</TableCell>
                        <TableCell align="right">
                          {t.realized_pnl != null ? fmtInr(Number(t.realized_pnl)) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {selectedRun && !isInProgress && (summary || selectedRun.status === "FAILED") && (
          <Accordion disableGutters onChange={handleDiagnosticsExpand}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Advanced Diagnostics</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {summary && (
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      1-minute candles
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {fmtPct(oneMinPct)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      5-minute derived candles
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {fmtPct(fiveMinPct)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Other/unclassified candles
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {fmtPct(missingPct)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Wins / Losses
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {fmtInt(summary.wins)} / {fmtInt(summary.losses)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Avg win / Avg loss
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {fmtInr(summary.avg_win)} / {fmtInr(summary.avg_loss)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Avg trade duration
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {fmtDurationMinutes(summary.avg_trade_duration_minutes)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Started
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRun.started_at ? formatIst(selectedRun.started_at) : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {selectedRun.completed_at ? formatIst(selectedRun.completed_at) : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Run ID
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ wordBreak: "break-all" }}>
                      {selectedRun.id}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Signal Diagnostics
              </Typography>
              {loadingDiagnostics ? (
                <CircularProgress size={20} />
              ) : diagnosticsError ? (
                <Alert severity="error">{diagnosticsError}</Alert>
              ) : diagnostics ? (
                <SignalDiagnosticsPanel
                  diagnostics={diagnostics}
                  showConditionFunnel={showConditionFunnel}
                  onToggleFunnel={() => setShowConditionFunnel((v) => !v)}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </Grid>
    </Grid>
  );
}

function MonitorTab({
  algoId,
  runtimeStatus,
  globalSettings,
  onChanged,
}: {
  algoId: string;
  runtimeStatus: RuntimeStatusResponse | null;
  globalSettings: GlobalSettingsRead | null;
  onChanged: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  // Defaults to PAPER -- the safest choice, and the only mode this button
  // could ever send before this fix. Selecting LIVE/ALL is an explicit,
  // deliberate admin action taken inside the confirm dialog below.
  const [emergencyExitMode, setEmergencyExitMode] = useState<"PAPER" | "LIVE" | "ALL">("PAPER");
  const [positions, setPositions] = useState<AlgoPosition[]>([]);
  const [orders, setOrders] = useState<AlgoOrder[]>([]);
  const [signals, setSignals] = useState<AlgoSignalLog[]>([]);
  const [positionStatusFilter, setPositionStatusFilter] = useState<string>("OPEN");

  const loadMonitor = useCallback(async () => {
    const [p, o, s] = await Promise.all([
      algoExecutionService.listPositions(algoId, { mode: "PAPER", status: positionStatusFilter || undefined }),
      algoExecutionService.listOrders(algoId, { mode: "PAPER" }),
      algoExecutionService.listSignals(algoId, { run_context: "LIVE" }),
    ]);
    setPositions(p);
    setOrders(o);
    setSignals(s);
  }, [algoId, positionStatusFilter]);

  useEffect(() => {
    loadMonitor().catch((err) => setError(extractErrorMessage(err)));
  }, [loadMonitor]);

  async function handleExecutionStatusToggle(nextLive: boolean) {
    setBusy(true);
    setError(null);
    try {
      await algoExecutionService.setExecutionStatus(algoId, { execution_status: nextLive ? "LIVE" : "DISABLED" });
      await onChanged();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGlobalKillSwitchToggle(enabled: boolean) {
    setBusy(true);
    setError(null);
    try {
      await algoExecutionService.updateGlobalSettings({ live_execution_enabled: enabled });
      await onChanged();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // "Pause New Entries" -- unlike the "Execution status: LIVE" switch
  // above (which only ever silences this algo's own platform test
  // instance), this reaches every customer's own activation of this algo
  // too: LiveFeedManager reads Algo.entries_paused fresh every candle for
  // both the admin's instance and every CustomerAlgoActivation dispatch.
  // Only suppresses a NEW entry -- any already-open position (customer or
  // admin) still exits normally via its own SL/target/EOD square-off.
  async function handleEntriesPausedToggle(paused: boolean) {
    setBusy(true);
    setError(null);
    try {
      await algoExecutionService.setEntriesPaused(algoId, { paused });
      await onChanged();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleEmergencyExit() {
    setBusy(true);
    setError(null);
    setConfirmExitOpen(false);
    try {
      await algoExecutionService.emergencyExitAll(algoId, { mode: emergencyExitMode });
      await Promise.all([onChanged(), loadMonitor()]);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Runtime controls
            </Typography>
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={runtimeStatus?.execution_status === "LIVE"}
                    onChange={(e) => handleExecutionStatusToggle(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Execution status: LIVE (positions still only ever execute in Paper mode — see kill switch below)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(globalSettings?.live_execution_enabled)}
                    onChange={(e) => handleGlobalKillSwitchToggle(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Global real-order kill switch (also requires the environment-level flag — real broker orders remain disabled in Phase 1 regardless of this setting)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(runtimeStatus?.entries_paused)}
                    onChange={(e) => handleEntriesPausedToggle(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Pause New Entries (stops new trades for EVERY customer activated on this algo; any already-open position still exits normally)"
              />
              <Button color="error" variant="outlined" onClick={() => setConfirmExitOpen(true)} disabled={busy}>
                Emergency Exit All Algo Positions
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Today's risk state
            </Typography>
            {runtimeStatus?.daily_risk_state ? (
              <Stack spacing={1}>
                <Typography variant="body2">Trades today: {runtimeStatus.daily_risk_state.trades_count}</Typography>
                <Typography variant="body2">Realized P&amp;L: {runtimeStatus.daily_risk_state.realized_pnl}</Typography>
                <Typography variant="body2">
                  Halted: {runtimeStatus.daily_risk_state.halted ? `yes (${runtimeStatus.daily_risk_state.halted_reason})` : "no"}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No trades recorded yet today.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Positions (Paper)
          </Typography>
          <Select size="small" value={positionStatusFilter} onChange={(e) => setPositionStatusFilter(e.target.value)}>
            <MenuItem value="OPEN">Open</MenuItem>
            <MenuItem value="CLOSED">Closed</MenuItem>
            <MenuItem value="">All</MenuItem>
          </Select>
        </Stack>
        {positions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No positions.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Option</TableCell>
                  <TableCell>Strike</TableCell>
                  <TableCell>Entry</TableCell>
                  <TableCell>SL / Target</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Entry / Exit time (IST)</TableCell>
                  <TableCell>Risk-check price source</TableCell>
                  <TableCell align="right">P&amp;L</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.option_type}</TableCell>
                    <TableCell>{p.strike}</TableCell>
                    <TableCell>{p.entry_price}</TableCell>
                    <TableCell>
                      {p.stop_loss} / {p.target}
                    </TableCell>
                    <TableCell>
                      <Chip label={p.status} size="small" color={p.status === "OPEN" ? "success" : "default"} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" component="div">
                        {formatIst(p.entry_time)}
                      </Typography>
                      {p.exit_time && (
                        <Typography variant="caption" component="div" color="text.secondary">
                          {formatIst(p.exit_time)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <PriceSourceIndicator
                        source={p.last_risk_check_price_source}
                        ageSeconds={p.last_risk_check_price_age_seconds}
                      />
                    </TableCell>
                    <TableCell align="right">{p.realized_pnl ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Orders (Paper)
            </Typography>
            {orders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No orders.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {orders.map((o) => (
                  <Box key={o.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                    <Typography variant="body2">
                      {o.side} — {o.status} — {o.filled_price ?? o.requested_price}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatIst(o.filled_at ?? o.created_at)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Recent signals (live)
            </Typography>
            {signals.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No signals recorded yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {signals.slice(0, 20).map((s) => (
                  <Box key={s.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                    <Typography variant="body2">
                      {formatIst(s.candle_start)} — {s.signal_type} — {s.direction}
                      {s.option_type ? ` (${s.option_type})` : ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={confirmExitOpen} onClose={() => setConfirmExitOpen(false)}>
        <DialogTitle>Emergency Exit All Algo Positions?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This force-closes every OPEN position for this algo in the mode you select below — including every
            customer's own activation, not just the platform's own instance. A LIVE position is closed with a real
            reversing broker order (never a fake paper-style fill). This action is logged and cannot be undone.
          </DialogContentText>
          <Select
            size="small"
            fullWidth
            value={emergencyExitMode}
            onChange={(e) => setEmergencyExitMode(e.target.value as "PAPER" | "LIVE" | "ALL")}
          >
            <MenuItem value="PAPER">Paper positions only</MenuItem>
            <MenuItem value="LIVE">Live positions only (real broker orders)</MenuItem>
            <MenuItem value="ALL">Both Paper and Live</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExitOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleEmergencyExit} disabled={busy}>
            Confirm Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AlgoManagementPage;
